import { useMemo } from 'react';

import useRoles from 'hooks/useRoles';

import { FREE, PREMIER, SEASONAL, TRIAL } from './constants';
import type { UseDisplaySubscriptionButtonsProps } from './types';

const useDisplaySubscriptionButtons = ({
    stripeSubscription
}: UseDisplaySubscriptionButtonsProps) => {
    const { canManageSubscriptions } = useRoles();

    const { hasSubscriptionAndPermissions, subscriptionTypes } = useMemo(() => {
        const hasSubscriptions = Boolean(stripeSubscription?.length);
        const subscriptionTypes = stripeSubscription?.map(({ subscription }) => subscription) || [];

        const hasSubscriptionAndPermissions = canManageSubscriptions && hasSubscriptions;

        return {
            hasSubscriptionAndPermissions,
            hasSubscriptions,
            subscriptionTypes
        };
    }, [stripeSubscription, canManageSubscriptions]);

    const displayUpgradeSubscriptionButton = useMemo(() => {
        if (!hasSubscriptionAndPermissions) {
            return false;
        }

        return subscriptionTypes.every(type => type === FREE);
    }, [hasSubscriptionAndPermissions, subscriptionTypes]);

    const displayAddSeasonalButton = useMemo(() => {
        if (!hasSubscriptionAndPermissions) {
            return false;
        }

        return subscriptionTypes.every(type => type === PREMIER);
    }, [hasSubscriptionAndPermissions, subscriptionTypes]);

    const displayManageSubscriptionButton = useMemo(() => {
        if (!hasSubscriptionAndPermissions) {
            return false;
        }

        return subscriptionTypes.some(type => [PREMIER, SEASONAL, TRIAL].includes(type));
    }, [hasSubscriptionAndPermissions, subscriptionTypes]);

    return {
        displayAddSeasonalButton,
        displayManageSubscriptionButton,
        displayUpgradeSubscriptionButton
    };
};

export default useDisplaySubscriptionButtons;
